import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTransactionDrawerStore } from '@hero/hooks-shared';
import { useTransactionApi } from '@hero/api-hooks';
import type { Transaction } from '@hero/schema';
import { Price } from '../../../app/Price';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { TransactionSummaryTab } from './TransactionSummaryTab';
import { TransactionInvoicesTab } from './TransactionInvoicesTab';
import { TransactionOrdersTab } from './TransactionOrdersTab';

export interface TransactionDrawerProps {
  displayForUser: 'customer' | 'carrier';
  syncUrl?: boolean | string;
}

interface TransactionDrawerContentProps {
  transaction: Transaction;
  displayForUser: 'customer' | 'carrier';
  onClose: () => void;
}

const TransactionDrawerContent: React.FC<TransactionDrawerContentProps> = ({
  transaction,
  displayForUser,
  onClose,
}) => {
  const { t } = useTranslation(['m-payment']);
  const [activeTab, setActiveTab] = useState('summary');

  const typeLabel = t(`m-payment:transaction.type.${transaction.type}`);

  const tabs: { id: string; label: string }[] = [
    { id: 'summary', label: t('m-payment:transaction.drawer.tabs.summary') },
    { id: 'invoices', label: t('m-payment:transaction.drawer.tabs.invoices') },
    { id: 'orders', label: t('m-payment:transaction.drawer.tabs.orders') },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return <TransactionSummaryTab transaction={transaction} />;
      case 'invoices':
        return <TransactionInvoicesTab transaction={transaction} />;
      case 'orders':
        return (
          <TransactionOrdersTab
            transactionId={transaction._id}
            displayForUser={displayForUser}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <DrawerHeader
        title={transaction.ref.main || transaction.ref.short}
        subtitle={<><Price amount={transaction.amount} currency={transaction.currency} /> · {typeLabel}</>}
        onClose={onClose}
      />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>{renderTab()}</DrawerContent>
    </>
  );
};

export const TransactionDrawer: React.FC<TransactionDrawerProps> = ({
  displayForUser,
  syncUrl,
}) => (
  <StackedDrawer<Transaction>
    useStore={useTransactionDrawerStore}
    useItemApi={id => useTransactionApi(id)}
    syncUrl={syncUrl}
    defaultParamKey="transactionId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Transaction Details"
    description="View transaction information"
    renderContent={(transaction, onClose) => (
      <TransactionDrawerContent
        transaction={transaction}
        displayForUser={displayForUser}
        onClose={onClose}
      />
    )}
  />
);
